import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { TopNavLayout } from "@/components/TopNavLayout";
import { AuthProvider, useAuth } from "@/state/AuthContext";
import { AdminCustomFieldsPage } from "@/pages/admin/AdminCustomFieldsPage";
import { AdminCustomersPage } from "@/pages/admin/AdminCustomersPage";
import { AdminLocationsPage } from "@/pages/admin/AdminLocationsPage";
import { AdminMailPage } from "@/pages/admin/AdminMailPage";
import { AdminMaintenancePage } from "@/pages/admin/AdminMaintenancePage";
import { AdminPartsPage } from "@/pages/admin/AdminPartsPage";
import { AdminReceiptOptionsPage } from "@/pages/admin/AdminReceiptOptionsPage";
import { AdminRequestOptionsPage } from "@/pages/admin/AdminRequestOptionsPage";
import { AdminRolesPage } from "@/pages/admin/AdminRolesPage";
import { AdminSystemsPage } from "@/pages/admin/AdminSystemsPage";
import { AdminTransactionTypesPage } from "@/pages/admin/AdminTransactionTypesPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { BillingPage } from "@/pages/BillingPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { CloseoutPage } from "@/pages/CloseoutPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { EquipmentRequestPage } from "@/pages/EquipmentRequestPage";
import { LandingPage } from "@/pages/LandingPage";
import { LoginPage } from "@/pages/LoginPage";
import { PartHistoryPage } from "@/pages/PartHistoryPage";
import { PlatformTenantDetailPage, PlatformTenantsPage } from "@/pages/platform/PlatformTenantsPage";
import { ReceiptsPage } from "@/pages/ReceiptsPage";
import { ReportsBuilderPage } from "@/pages/ReportsBuilderPage";
import { ReportsWeeklyPage } from "@/pages/ReportsWeeklyPage";
import { SignupPage } from "@/pages/SignupPage";
import { SystemsPage } from "@/pages/SystemsPage";
import { TeamPage } from "@/pages/TeamPage";
import { TransactionsPage } from "@/pages/TransactionsPage";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

function PrivateRoute({
  children,
  allowLocked = false,
}: {
  children: React.ReactNode;
  allowLocked?: boolean;
}) {
  const { token, loading, tenant, user } = useAuth();
  const location = useLocation();
  const onBillingPage = location.pathname === "/admin/billing";

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  if (!token) return <Navigate to="/login" replace />;

  return (
    <MaintenanceGate>
      {tenant?.subscriptionStatus === "locked" &&
      !allowLocked &&
      !onBillingPage &&
      !user?.isPlatformAdmin ? (
        <Navigate to="/admin/billing" replace />
      ) : (
        children
      )}
    </MaintenanceGate>
  );
}

function RedirectPlatformTenant() {
  const { tenantId } = useParams();
  return <Navigate to={`/admin/platform/tenants/${tenantId}`} replace />;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="settings/billing" element={<Navigate to="/admin/billing" replace />} />
      <Route path="settings/team" element={<Navigate to="/admin/team" replace />} />
      <Route path="platform" element={<Navigate to="/admin/platform" replace />} />
      <Route path="platform/tenants/:tenantId" element={<RedirectPlatformTenant />} />
      <Route
        element={
          <PrivateRoute>
            <TopNavLayout />
          </PrivateRoute>
        }
      >
        <Route
          path="dashboard"
          element={
            hasPermission(user, PERMISSIONS.dashboard) ? (
              <DashboardPage />
            ) : (
              <Navigate to="/systems" replace />
            )
          }
        />
        <Route path="receipts" element={<ReceiptsPage />} />
        <Route path="receipts/:receiptId" element={<ReceiptsPage />} />
        <Route path="equipment-request" element={<EquipmentRequestPage />} />
        <Route path="equipment-request/:requestId" element={<EquipmentRequestPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="equipment-checkout" element={<CheckoutPage />} />
        <Route path="equipment-checkout/:checkoutId" element={<CheckoutPage />} />
        <Route path="closeout" element={<CloseoutPage />} />
        <Route path="reports/weekly" element={<ReportsWeeklyPage />} />
        <Route path="reports/builder" element={<ReportsBuilderPage />} />
        <Route path="systems" element={<SystemsPage />} />
        <Route path="systems/:systemId" element={<SystemsPage />} />
        <Route path="systems/:systemId/parts/:partId/history" element={<PartHistoryPage />} />
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="platform" element={<PlatformTenantsPage />} />
          <Route path="platform/tenants/:tenantId" element={<PlatformTenantDetailPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="roles" element={<AdminRolesPage />} />
          <Route path="systems" element={<AdminSystemsPage />} />
          <Route path="parts" element={<AdminPartsPage />} />
          <Route path="locations" element={<AdminLocationsPage />} />
          <Route path="custom-fields" element={<AdminCustomFieldsPage />} />
          <Route path="transaction-types" element={<AdminTransactionTypesPage />} />
          <Route path="receipt-options" element={<AdminReceiptOptionsPage />} />
          <Route path="request-options" element={<AdminRequestOptionsPage />} />
          <Route path="customers" element={<AdminCustomersPage />} />
          <Route path="maintenance" element={<AdminMaintenancePage />} />
          <Route path="mail" element={<AdminMailPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
